import React, { useState, useEffect } from 'react';
import { ref, onValue, set, get, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { realtimeDb } from '../firebase';
import { 
  Volume2, 
  VolumeX, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Play,
  Pause,
  Settings,
  Music,
  Bell,
  Zap,
  Star,
  Download,
  Upload
} from 'lucide-react';

interface AudioEffect {
  id: string;
  name: string;
  type: 'coin' | 'game' | 'ui' | 'ambient';
  category: 'basic' | 'premium' | 'seasonal';
  description: string;
  fileUrl?: string;
  duration: number; // másodpercekben
  volume: number; // 0-100
  isActive: boolean;
  isPopular?: boolean;
  price?: number;
  currency?: string;
  tags: string[];
  createdAt: number;
}

interface AudioData {
  [effectId: string]: AudioEffect;
}

const AudioManagement: React.FC = () => {
  const [audioData, setAudioData] = useState<AudioData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | 'coin' | 'game' | 'ui' | 'ambient'>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'basic' | 'premium' | 'seasonal'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEffect, setEditingEffect] = useState<AudioEffect | null>(null);
  const [playingEffect, setPlayingEffect] = useState<string | null>(null);
  const auth = getAuth();

  // Alapértelmezett hangeffektek
  const defaultEffects: AudioEffect[] = [
    {
      id: 'coin_golden_jingle',
      name: 'Aranyos Csörgés',
      type: 'coin',
      category: 'basic',
      description: 'Aranytallér vásárlásakor lejátszott aranyos csörgés',
      duration: 2.5,
      volume: 80,
      isActive: true,
      isPopular: true,
      tags: ['coin', 'purchase', 'golden'],
      createdAt: Date.now()
    },
    {
      id: 'game_ball_hit',
      name: 'Golyó Ütés',
      type: 'game',
      category: 'basic',
      description: 'Golyó ütésének hangja',
      duration: 1.2,
      volume: 70,
      isActive: true,
      tags: ['game', 'ball', 'hit'],
      createdAt: Date.now()
    },
    {
      id: 'game_pocket',
      name: 'Zsebbe Esés',
      type: 'game',
      category: 'basic',
      description: 'Golyó zsebbe esésének hangja',
      duration: 1.8,
      volume: 75,
      isActive: true,
      tags: ['game', 'pocket', 'success'],
      createdAt: Date.now()
    },
    {
      id: 'ui_button_click',
      name: 'Gomb Kattintás',
      type: 'ui',
      category: 'basic',
      description: 'UI gomb kattintásának hangja',
      duration: 0.3,
      volume: 60,
      isActive: true,
      tags: ['ui', 'button', 'click'],
      createdAt: Date.now()
    },
    {
      id: 'ui_menu_open',
      name: 'Menü Megnyitás',
      type: 'ui',
      category: 'basic',
      description: 'Menü megnyitásának hangja',
      duration: 0.5,
      volume: 65,
      isActive: true,
      tags: ['ui', 'menu', 'open'],
      createdAt: Date.now()
    },
    {
      id: 'ambient_crowd',
      name: 'Tömeg Hang',
      type: 'ambient',
      category: 'premium',
      description: 'Háttérben hallható tömeg hang',
      duration: 30.0,
      volume: 40,
      isActive: true,
      price: 100,
      currency: 'USD',
      tags: ['ambient', 'crowd', 'background'],
      createdAt: Date.now()
    },
    {
      id: 'game_victory',
      name: 'Győzelem',
      type: 'game',
      category: 'premium',
      description: 'Játék megnyerésének hangja',
      duration: 3.0,
      volume: 85,
      isActive: true,
      isPopular: true,
      price: 50,
      currency: 'USD',
      tags: ['game', 'victory', 'win'],
      createdAt: Date.now()
    },
    {
      id: 'seasonal_christmas',
      name: 'Karácsonyi Hang',
      type: 'ambient',
      category: 'seasonal',
      description: 'Karácsonyi háttérzene',
      duration: 60.0,
      volume: 50,
      isActive: false,
      tags: ['seasonal', 'christmas', 'music'],
      createdAt: Date.now()
    }
  ];

  useEffect(() => {
    if (!auth.currentUser) return;

    const audioRef = ref(realtimeDb, 'audio');

    const unsubscribe = onValue(audioRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAudioData(data);
      } else {
        // Alapértelmezett effektek betöltése
        const defaultData: AudioData = {};
        defaultEffects.forEach(effect => {
          defaultData[effect.id] = effect;
        });
        setAudioData(defaultData);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching audio data:', error);
      
      // Mock adatok fejlesztéshez
      const mockData: AudioData = {};
      defaultEffects.forEach(effect => {
        mockData[effect.id] = effect;
      });
      
      setAudioData(mockData);
      setError('Firebase jogosultsági hiba - mock adatok használata');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth.currentUser]);

  const addAudioEffect = async (effect: Omit<AudioEffect, 'id' | 'createdAt'>) => {
    try {
      const newEffect: AudioEffect = {
        ...effect,
        id: `${effect.type}_${Date.now()}`,
        createdAt: Date.now()
      };

      const effectRef = ref(realtimeDb, `audio/${newEffect.id}`);
      await set(effectRef, newEffect);
      console.log('Audio effect added successfully');
    } catch (error) {
      console.error('Error adding audio effect:', error);
      setError('Hiba történt az effekt hozzáadása során');
    }
  };

  const updateAudioEffect = async (effect: AudioEffect) => {
    try {
      const effectRef = ref(realtimeDb, `audio/${effect.id}`);
      await set(effectRef, effect);
      console.log('Audio effect updated successfully');
    } catch (error) {
      console.error('Error updating audio effect:', error);
      setError('Hiba történt az effekt frissítése során');
    }
  };

  const deleteAudioEffect = async (effectId: string) => {
    if (window.confirm(`Biztosan törölni szeretnéd ezt az effektet?`)) {
      try {
        await remove(ref(realtimeDb, `audio/${effectId}`));
        console.log('Audio effect deleted successfully');
      } catch (error) {
        console.error('Error deleting audio effect:', error);
        setError('Hiba történt az effekt törlése során');
      }
    }
  };

  const playAudioEffect = (effectId: string) => {
    setPlayingEffect(effectId);
    // Itt implementálnánk a tényleges hang lejátszását
    setTimeout(() => {
      setPlayingEffect(null);
    }, 2000);
  };

  const getTotalEffects = () => {
    return Object.keys(audioData).length;
  };

  const getActiveEffects = () => {
    return Object.values(audioData).filter(effect => effect.isActive).length;
  };

  const getEffectsByType = (type: string) => {
    return Object.values(audioData).filter(effect => 
      type === 'all' || effect.type === type
    ).length;
  };

  const getTotalDuration = () => {
    return Object.values(audioData).reduce((total, effect) => {
      return total + effect.duration;
    }, 0);
  };

  const filteredEffects = Object.entries(audioData).filter(([id, effect]) => {
    const searchLower = searchTerm.toLowerCase();
    return effect.name.toLowerCase().includes(searchLower) ||
           effect.description.toLowerCase().includes(searchLower) ||
           effect.tags.some(tag => tag.toLowerCase().includes(searchLower));
    
    const matchesType = selectedType === 'all' || effect.type === selectedType;
    const matchesCategory = selectedCategory === 'all' || effect.category === selectedCategory;
    
    return matchesType && matchesCategory;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'coin':
        return <Bell className="h-4 w-4" />;
      case 'game':
        return <Zap className="h-4 w-4" />;
      case 'ui':
        return <Settings className="h-4 w-4" />;
      case 'ambient':
        return <Music className="h-4 w-4" />;
      default:
        return <Volume2 className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'basic':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'premium':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'seasonal':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Hangeffektek Kezelése</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Játék hangok és effektek kezelése
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button 
            onClick={() => {
              setEditingEffect(null);
              setShowAddModal(true);
            }}
            className="btn-primary w-full sm:w-auto px-4 py-3 sm:py-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Új Effekt
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Volume2 className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Összes Effekt</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getTotalEffects()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Play className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktív Effektek</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getActiveEffects()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Music className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Játék Hangok</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getEffectsByType('game')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aranytallér Hangok</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getEffectsByType('coin')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés név, leírás vagy címke alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden típus</option>
                <option value="coin">Aranytallér</option>
                <option value="game">Játék</option>
                <option value="ui">Felület</option>
                <option value="ambient">Háttér</option>
              </select>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as any)}
                className="form-input"
              >
                <option value="all">Minden kategória</option>
                <option value="basic">Alap</option>
                <option value="premium">Prémium</option>
                <option value="seasonal">Szezonal</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Effects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredEffects.map(([id, effect]) => (
          <div key={id} className={`card ${effect.isPopular ? 'ring-2 ring-yellow-500' : ''}`}>
            <div className="card-body">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(effect.type)}
                  <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(effect.category)}`}>
                    {effect.category}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => playAudioEffect(id)}
                    className={`p-1 rounded ${
                      playingEffect === id 
                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                        : 'text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400'
                    }`}
                  >
                    {playingEffect === id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingEffect(effect);
                      setShowAddModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteAudioEffect(id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {effect.name}
                {effect.isPopular && <Star className="inline h-4 w-4 text-yellow-500 ml-1" />}
              </h4>
              
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {effect.description}
              </p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Időtartam:</span>
                  <span className="text-gray-900 dark:text-white">{effect.duration}s</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Hangerő:</span>
                  <span className="text-gray-900 dark:text-white">{effect.volume}%</span>
                </div>
                
                {effect.price && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Ár:</span>
                    <span className="text-gray-900 dark:text-white">{effect.price} {effect.currency}</span>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1 mt-2">
                  {effect.tags.map((tag, index) => (
                    <span key={index} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    effect.isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {effect.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(effect.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {editingEffect ? 'Effekt Szerkesztése' : 'Új Effekt Hozzáadása'}
              </h3>
              
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Név</label>
                    <input
                      type="text"
                      defaultValue={editingEffect?.name || ''}
                      className="form-input"
                      placeholder="Effekt neve"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Típus</label>
                    <select
                      defaultValue={editingEffect?.type || 'game'}
                      className="form-input"
                    >
                      <option value="coin">Aranytallér</option>
                      <option value="game">Játék</option>
                      <option value="ui">Felület</option>
                      <option value="ambient">Háttér</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Kategória</label>
                    <select
                      defaultValue={editingEffect?.category || 'basic'}
                      className="form-input"
                    >
                      <option value="basic">Alap</option>
                      <option value="premium">Prémium</option>
                      <option value="seasonal">Szezonal</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Időtartam (másodperc)</label>
                    <input
                      type="number"
                      step="0.1"
                      defaultValue={editingEffect?.duration || 1.0}
                      className="form-input"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Hangerő (0-100)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      defaultValue={editingEffect?.volume || 70}
                      className="form-input"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Ár (opcionális)</label>
                    <input
                      type="number"
                      defaultValue={editingEffect?.price || 0}
                      className="form-input"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="form-label">Leírás</label>
                  <textarea
                    defaultValue={editingEffect?.description || ''}
                    className="form-input"
                    rows={3}
                    placeholder="Effekt leírása"
                  />
                </div>
                
                <div>
                  <label className="form-label">Címkék (vesszővel elválasztva)</label>
                  <input
                    type="text"
                    defaultValue={editingEffect?.tags?.join(', ') || ''}
                    className="form-input"
                    placeholder="pl. game, ball, hit"
                  />
                </div>
                
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked={editingEffect?.isActive ?? true}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Aktív</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked={editingEffect?.isPopular || false}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Népszerű</span>
                  </label>
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary"
                >
                  Mégse
                </button>
                <button
                  onClick={() => {
                    // Itt implementálnánk a mentést
                    setShowAddModal(false);
                  }}
                  className="btn-primary"
                >
                  {editingEffect ? 'Frissítés' : 'Hozzáadás'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Hiba
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioManagement; 